/**
 * Element-context action handlers — wires the three menu events emitted by
 * element-context-menu.js to the mutation primitives in element-insert.js.
 *
 *   element-menu:add-after  — opens the platform picker, then inserts a sibling
 *   element-menu:duplicate  — clones the repeat root in place
 *   element-menu:toggle-visibility — toggles the existing hidden override
 *   element-menu:delete     — removes the repeat root + prunes stale overrides
 *
 * All three resolve the host section via resolveSectionByInstance (handling
 * both `pages[].sections[]` and `sharedSections.{nav,footer}`), commit through
 * `store.update` (or `store.batch` for delete's two-path update), trigger a
 * preview re-render, and reselect the resulting editable when relevant.
 */

import { reservedEditableIds } from '../shared/editable-id-repair.js';
import { editableIdRepairAllocationReady } from './editable-id-repair-admission.js';
import { eventBus } from '../core/events.js';
import { requestPanelOpen } from './glass-panels.js';
import { store } from '../core/state.js';
import { reportError } from '../core/report-error.js';
import { bridge } from './bridge.js';
import { escapeForAttrSelector } from './editable-utils.js';
import { resolveSectionByInstance } from './section-utils.js';
import {
  insertSiblingNearEid,
  duplicateNearEid,
  removeNearEid,
  pruneOverrides,
  cloneOverridesViaEidMap,
  serializeSubtreeAroundEid,
  insertSerializedSubtreeNearEid,
  applyOverridesCloneToSection,
  moveAdjacentSiblingPreservingEids
} from './element-insert.js';
import { buildBulkElementDelete, buildBulkElementDuplicate } from './bulk-element-ops.js';
import {
  buildSnapshotFromSerialization,
  clearClipboard,
  getClipboard,
  setClipboardSnapshot
} from './element-clipboard.js';
import {
  enterPaintMode,
  applyToTarget as applyPaintToTarget,
  isPaintModeActive,
  exitPaintMode
} from './format-painter.js';
import { getSocialPlatforms, socialLinkAdapter } from './adapters/social-link-adapter.js';
import { getAdapterByKind } from './pattern-adapters.js';
import { openSocialLinkDrawer } from '../ui/section-properties/social-link-drawer.js';
import { openSocialLinkPicker, closeSocialLinkPicker } from '../ui/social-link-picker.js';

let _initialized = false;
let _showToast = null;
let _showUndoToast = null;
let _pendingReselectCancel = null;
let _pendingReselectInstanceId = null;
let _pendingInsert = null;

function admitRepairAllocation(section) {
  return editableIdRepairAllocationReady(section, store.getState(), (message, options) =>
    _showToast?.(message, options)
  );
}

const RESELECT_AFTER_RENDER_FALLBACK_MS = 450;

/** Subscribe once. Idempotent. */
export function initElementActions(opts = {}) {
  if (typeof opts.showToast === 'function') _showToast = opts.showToast;
  if (typeof opts.showUndoToast === 'function') _showUndoToast = opts.showUndoToast;
  if (_initialized) return;
  _initialized = true;
  eventBus.on('element-menu:add-after', _onAddAfter);
  eventBus.on('element-menu:entrance', _onEntrance);
  eventBus.on('element-menu:duplicate', _onDuplicate);
  eventBus.on('element-menu:copy', _onCopy);
  eventBus.on('element-menu:cut', _onCut);
  eventBus.on('element-menu:paste', _onPaste);
  eventBus.on('element-menu:copy-style', _onCopyStyle);
  eventBus.on('element-menu:paste-style', _onPasteStyle);
  eventBus.on('element-menu:add-ai-context', _onAddAiContext);
  eventBus.on('element-menu:toggle-visibility', _onToggleVisibility);
  eventBus.on('element-menu:delete', _onDelete);
  eventBus.on('element-menu:move-prev', _onMovePrev);
  eventBus.on('element-menu:move-next', _onMoveNext);
  eventBus.on('element:move-request', _onMoveElement);
  eventBus.on('iframe:reorder-commit', _onReorder);
  eventBus.on('editable-ids:repaired', _onEditableIdsRepaired);
}

export function destroyElementActions() {
  if (!_initialized) return;
  eventBus.off('element-menu:add-after', _onAddAfter);
  eventBus.off('element-menu:entrance', _onEntrance);
  eventBus.off('element-menu:duplicate', _onDuplicate);
  eventBus.off('element-menu:copy', _onCopy);
  eventBus.off('element-menu:cut', _onCut);
  eventBus.off('element-menu:paste', _onPaste);
  eventBus.off('element-menu:copy-style', _onCopyStyle);
  eventBus.off('element-menu:paste-style', _onPasteStyle);
  eventBus.off('element-menu:add-ai-context', _onAddAiContext);
  eventBus.off('element-menu:toggle-visibility', _onToggleVisibility);
  eventBus.off('element-menu:delete', _onDelete);
  eventBus.off('element-menu:move-prev', _onMovePrev);
  eventBus.off('element-menu:move-next', _onMoveNext);
  eventBus.off('element:move-request', _onMoveElement);
  eventBus.off('iframe:reorder-commit', _onReorder);
  eventBus.off('editable-ids:repaired', _onEditableIdsRepaired);
  _pendingInsert = null;
  closeSocialLinkPicker();
  _initialized = false;
  if (_pendingReselectCancel) {
    _pendingReselectCancel();
    _pendingReselectCancel = null;
  }
}

// EA-9: the menu item is a DOOR, not a control — it opens the element's glass
// panel, where the shared entrance row (glass-panel-entrance-row.js) lives.
function _onEntrance({ payload } = {}) {
  if (!payload) return;
  requestPanelOpen(payload, { explicit: true });
}

function _onEditableIdsRepaired({ instanceIds = [] } = {}) {
  if (instanceIds.includes(_pendingReselectInstanceId)) _pendingReselectCancel?.();
  if (instanceIds.includes(_pendingInsert?.instanceId)) {
    _pendingInsert = null;
    closeSocialLinkPicker();
  }
}

function _onAddAfter({ payload, adapterKind } = {}) {
  console.log('[element-actions] add-after fired', { adapterKind, payload });
  if (!payload) {
    console.warn('[element-actions] add-after bail: no payload');
    return;
  }
  // Phase 4 only wires social-link end-to-end. Other adapter kinds become
  // active as their pickers/recipes ship.
  if (adapterKind !== 'social-link') {
    console.warn('[element-actions] add-after not wired for adapter:', adapterKind);
    return;
  }
  const target = resolveSectionByInstance(payload.instanceId);
  if (!target?.section?.html) {
    console.warn('[element-actions] add-after bail: cannot resolve section', payload.instanceId);
    return;
  }

  if (getSocialPlatforms().length === 0) {
    console.warn('[element-actions] add-after bail: social-icon-library not loaded yet');
    _showToast?.('Social icons are still loading. Try again in a moment.', { type: 'error' });
    return;
  }

  const pending = { instanceId: payload.instanceId };
  _pendingInsert = pending;
  openSocialLinkPicker(_anchorRectFromPayload(payload), {
    onPick: (platformId) => {
      if (_pendingInsert !== pending) return;
      _pendingInsert = null;
      const subtreeHtml = socialLinkAdapter.contentRecipe({ platform: platformId });
      if (!subtreeHtml) {
        _showToast?.('Could not create that social icon', { type: 'error' });
        return;
      }

      // Re-resolve at commit time in case the section moved (very unlikely but cheap).
      const fresh = resolveSectionByInstance(payload.instanceId);
      if (!fresh?.section?.html) return;

      if (!admitRepairAllocation(fresh.section)) return;
      const result = insertSiblingNearEid(fresh.section.html, payload.eid, {
        itemSelector: socialLinkAdapter.itemSelector,
        editableIdRepairs: fresh.section.editableIdRepairs,
        position: 'after',
        subtreeHtml
      });
      if (!result.updatedHtml) {
        console.warn('[element-actions] insertSiblingNearEid returned null');
        return;
      }
      store.update(fresh.path, result.updatedHtml, 'Add social link');
      eventBus.emit('preview:sync-section-html', {
        instanceId: payload.instanceId,
        syncOverrides: false
      });
      // PP-2: an add ends with a way back; the undo IS the confirmation.
      if (_showUndoToast) _showUndoToast('Social icon added', { id: 'undo-add-element' });
      else _showToast?.('Social icon added', { type: 'success' });
      const inserted = {
        instanceId: payload.instanceId,
        adapterKind,
        newRootEid: result.newRootEid,
        newPrimaryEid: result.newPrimaryEid,
        rect: payload.rect
      };
      eventBus.emit('element-actions:inserted', inserted);
      _reselectAfterRender(payload.instanceId, result.newPrimaryEid, () => {
        openSocialLinkDrawer({
          instanceId: payload.instanceId,
          eid: result.newPrimaryEid,
          sourceRect: _anchorRectFromPayload(payload),
          showToast: _showToast
        });
      });
    }
  });
}

function _onDuplicate({ payload, adapterKind } = {}) {
  if (!payload) return;
  const target = resolveSectionByInstance(payload.instanceId);
  if (!target?.section?.html) return;

  const itemSelector = _itemSelectorFor(adapterKind, payload);
  if (!itemSelector) {
    console.warn('[element-actions] duplicate: no itemSelector for adapter', adapterKind);
    return;
  }

  if (!admitRepairAllocation(target.section)) return;
  const result = duplicateNearEid(target.section.html, payload.eid, {
    editableIdRepairs: target.section.editableIdRepairs,
    itemSelector,
    position: 'after'
  });
  if (!result.updatedHtml) {
    console.warn('[element-actions] duplicateNearEid returned null');
    return;
  }

  // Clone per-element overrides via eidMap so the duplicate keeps its styling.
  const overridesPatch = cloneOverridesViaEidMap(target.section, result.eidMap);
  const mutations = [{ path: target.path, value: result.updatedHtml }];
  if (overridesPatch) {
    mutations.push({ path: `${target.sectionPath}.overrides`, value: overridesPatch });
  }
  store.batch(mutations, 'Duplicate element');
  eventBus.emit('preview:sync-section-html', {
    instanceId: payload.instanceId,
    syncOverrides: !!overridesPatch
  });
  if (_showUndoToast) _showUndoToast('Element duplicated', { id: 'undo-duplicate-element' });
  else _showToast?.('Element duplicated', { type: 'success' });
  _reselectAfterRender(payload.instanceId, result.newPrimaryEid, () => {
    if (adapterKind === 'social-link') {
      openSocialLinkDrawer({
        instanceId: payload.instanceId,
        eid: result.newPrimaryEid,
        sourceRect: _anchorRectFromPayload(payload),
        showToast: _showToast
      });
    }
  });
}

/**
 * "Add to AI chat" — hand the element to the agent composer as a context chip.
 * The panel rebuilds and validates the ref itself; this only forwards the ids
 * and the label the iframe already computed for the hover pill.
 */
function _onAddAiContext({ payload } = {}) {
  if (!payload?.instanceId || !payload?.eid) return;
  eventBus.emit('agent:add-context-ref', {
    ref: {
      kind: 'element',
      instanceId: payload.instanceId,
      eid: payload.eid,
      editableType: payload.editableType || '',
      tagName: payload.tagName || '',
      label: payload.label || ''
    }
  });
}

function _onToggleVisibility({ payload, adapterKind } = {}) {
  try {
    if (!payload) return;
    const target = resolveSectionByInstance(payload.instanceId);
    if (!target?.section?.html) return;

    if (!admitRepairAllocation(target.section)) return;
    const visibilityTarget = _visibilityTargetFor(
      target.section.html,
      payload.eid,
      adapterKind,
      target.section.editableIdRepairs
    );
    if (!visibilityTarget?.eid) return;

    const current = target.section.overrides?.elements?.[visibilityTarget.eid]?.hidden === true;
    const nextHidden = !current;
    const nextOverrides = _withElementHidden(
      target.section.overrides,
      visibilityTarget.eid,
      nextHidden
    );

    const mutations = [];
    if (visibilityTarget.updatedHtml && visibilityTarget.updatedHtml !== target.section.html) {
      mutations.push({ path: target.path, value: visibilityTarget.updatedHtml });
    }
    mutations.push({ path: target.overridesPath, value: nextOverrides });

    store.batch(mutations, nextHidden ? 'Hide element' : 'Show element');
    if (visibilityTarget.updatedHtml) {
      eventBus.emit('preview:sync-section-html', { instanceId: payload.instanceId });
    } else {
      eventBus.emit('theme:override-changed');
    }

    if (nextHidden) {
      if (_showUndoToast) _showUndoToast('Element hidden', { id: 'undo-hide-element' });
      else _showToast?.('Element hidden', { type: 'info' });
    } else {
      _showToast?.('Element shown', { type: 'success' });
      _reselectAfterRender(payload.instanceId, visibilityTarget.eid);
    }
  } catch (err) {
    console.error('[element-actions] visibility toggle failed:', err);
    _showToast?.('Could not update element visibility', { type: 'error' });
  }
}

async function _onDelete({ payload, adapterKind } = {}) {
  try {
    if (!payload) return;
    const target = resolveSectionByInstance(payload.instanceId);
    if (!target?.section?.html) return;

    const itemSelector = _itemSelectorFor(adapterKind, payload);
    if (!itemSelector) {
      console.warn('[element-actions] delete: no itemSelector for adapter', adapterKind);
      return;
    }

    // Delete policy (Polish Phase 8, tiered): element-level deletes are small and
    // single-Cmd+Z undoable, so they skip the confirm dialog and rely on the undo
    // toast below. Section-level deletes keep their rich confirm in section-manager.
    const result = removeNearEid(target.section.html, payload.eid, { itemSelector });
    if (!result.updatedHtml) {
      console.warn('[element-actions] removeNearEid returned null');
      return;
    }

    const mutations = [{ path: target.path, value: result.updatedHtml }];
    const pruned = pruneOverrides(target.section, result.removedEids);
    if (pruned !== target.section) {
      mutations.push({ path: `${target.sectionPath}.overrides`, value: pruned.overrides });
    }
    const overridesChanged = pruned !== target.section;
    store.batch(mutations, 'Delete element');
    eventBus.emit('preview:sync-section-html', {
      instanceId: payload.instanceId,
      syncOverrides: overridesChanged
    });
    if (_showUndoToast) _showUndoToast('Element deleted', { id: 'undo-delete-element' });
    else _showToast?.('Element deleted', { type: 'info' });
  } catch (err) {
    console.error('[element-actions] delete failed:', err);
    _showToast?.('Could not delete element', { type: 'error' });
  }
}

/**
 * Bulk delete of repeat-root siblings (Polish 16C1). N units become N pure
 * string transforms over ONE accumulating HTML string and a SINGLE
 * `store.batch` — one history entry, one undo, one resync, one toast, where the
 * loop-over-`_onDelete` shape would have produced N of each.
 *
 * All-or-nothing: `buildBulkElementDelete` returns null if any unit fails to
 * resolve, and nothing is written. Overrides are pruned once, on the union of
 * every removed eid.
 *
 * @param {{instanceId: string, unitEids: string[], itemSelector: string}} spec
 * @returns {{ok: boolean, count: number}}
 */
export function bulkDeleteElements(spec = {}) {
  const { instanceId, unitEids, itemSelector } = spec;
  const eids = (Array.isArray(unitEids) ? unitEids : []).filter(Boolean);
  const fail = (err) => {
    reportError('Delete elements', err, {
      user: 'toast',
      message: "Couldn't delete those elements"
    });
    return { ok: false, count: 0 };
  };
  if (!instanceId || !itemSelector || eids.length === 0) {
    return fail(new Error('bulk delete: incomplete spec'));
  }

  const target = resolveSectionByInstance(instanceId);
  if (!target?.section?.html) return fail(new Error('bulk delete: section did not resolve'));

  let result;
  try {
    result = buildBulkElementDelete(target.section.html, eids, itemSelector);
  } catch (err) {
    return fail(err);
  }
  if (!result) return fail(new Error('bulk delete: a unit did not resolve'));

  const mutations = [{ path: target.path, value: result.updatedHtml }];
  const pruned = pruneOverrides(target.section, result.removedEids);
  const overridesChanged = pruned !== target.section;
  if (overridesChanged) {
    mutations.push({ path: `${target.sectionPath}.overrides`, value: pruned.overrides });
  }
  // Label is deliberately NOT "…section(s)": requiresFullRenderForLabel matches
  // that shape and would force a whole-preview rebuild instead of the surgical
  // per-instance resync this op emits itself.
  if (!store.batch(mutations, `Delete ${eids.length} elements`)) {
    return fail(new Error('bulk delete commit rejected'));
  }
  eventBus.emit('preview:sync-section-html', {
    instanceId,
    syncOverrides: overridesChanged
  });
  const message = `${eids.length} elements deleted`;
  if (_showUndoToast) _showUndoToast(message, { id: 'undo-delete-elements' });
  else _showToast?.(message, { type: 'info' });
  return { ok: true, count: eids.length };
}

/**
 * Bulk duplicate of repeat-root siblings (Polish 16C1). Each clone lands
 * immediately after its own original, so N=1 is slot-identical to
 * `_onDuplicate`. Per-element overrides migrate once through the merged eidMap.
 *
 * @param {{instanceId: string, unitEids: string[], itemSelector: string}} spec
 * @returns {{ok: boolean, count: number, newUnitEids: string[]}}
 */
export function bulkDuplicateElements(spec = {}) {
  const { instanceId, unitEids, itemSelector } = spec;
  const eids = (Array.isArray(unitEids) ? unitEids : []).filter(Boolean);
  const fail = (err) => {
    reportError('Duplicate elements', err, {
      user: 'toast',
      message: "Couldn't duplicate those elements"
    });
    return { ok: false, count: 0, newUnitEids: [] };
  };
  if (!instanceId || !itemSelector || eids.length === 0) {
    return fail(new Error('bulk duplicate: incomplete spec'));
  }

  const target = resolveSectionByInstance(instanceId);
  if (!target?.section?.html) return fail(new Error('bulk duplicate: section did not resolve'));

  if (!admitRepairAllocation(target.section)) return { ok: false, count: 0, newUnitEids: [] };
  let result;
  try {
    result = buildBulkElementDuplicate(target.section.html, eids, itemSelector, {
      editableIdRepairs: target.section.editableIdRepairs
    });
  } catch (err) {
    return fail(err);
  }
  if (!result) return fail(new Error('bulk duplicate: a unit did not resolve'));

  const mutations = [{ path: target.path, value: result.updatedHtml }];
  const overridesPatch = cloneOverridesViaEidMap(target.section, result.eidMap);
  if (overridesPatch) {
    mutations.push({ path: `${target.sectionPath}.overrides`, value: overridesPatch });
  }
  if (!store.batch(mutations, `Duplicate ${eids.length} elements`)) {
    return fail(new Error('bulk duplicate commit rejected'));
  }
  eventBus.emit('preview:sync-section-html', {
    instanceId,
    syncOverrides: !!overridesPatch
  });
  if (_showUndoToast) {
    _showUndoToast(`${eids.length} elements duplicated`, { id: 'undo-duplicate-elements' });
  } else {
    _showToast?.(`${eids.length} elements duplicated`, { type: 'success' });
  }
  return { ok: true, count: eids.length, newUnitEids: result.newUnitEids };
}

// The element clipboard is a pure in-tab store with no event surface; emit
// from here so the clipboard tray (panel-bottom-zone) can show/hide the
// element card the same way it reacts to `clipboard:changed` for sections.
function _emitElementClipboardChanged(snapshot) {
  eventBus.emit('clipboard:element-changed', {
    hasClipboard: !!snapshot,
    adapterKind: snapshot?.adapterKind || null
  });
}

function _collectIdsFromHtml(html, ids) {
  if (!html || typeof DOMParser === 'undefined') return;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    for (const el of doc.querySelectorAll('[id]')) {
      const id = el.getAttribute('id');
      if (id) ids.add(id);
    }
  } catch (err) {
    console.warn('[element-actions] could not collect identity ids:', err);
  }
}

function _collectProjectIdentityIds() {
  const ids = new Set();
  const state = store.getState?.();
  const shared = state?.sharedSections || {};
  _collectIdsFromHtml(shared.navigation?.html, ids);
  _collectIdsFromHtml(shared.footer?.html, ids);
  for (const page of state?.pages || []) {
    for (const section of page?.sections || []) {
      _collectIdsFromHtml(section?.html, ids);
    }
  }
  return Array.from(ids);
}

function _onCopy({ payload, adapterKind } = {}) {
  const snapshot = _captureClipboardSnapshot(payload, adapterKind);
  if (!snapshot) {
    _showToast?.('Could not copy this element', { type: 'error' });
    return;
  }
  setClipboardSnapshot(snapshot);
  _emitElementClipboardChanged(snapshot);
  _showToast?.('Element copied', { type: 'success' });
}

function _onCut({ payload, adapterKind } = {}) {
  if (!payload) return;
  const target = resolveSectionByInstance(payload.instanceId);
  if (!target?.section?.html) return;
  const itemSelector = _itemSelectorFor(adapterKind, payload);
  if (!itemSelector) {
    console.warn('[element-actions] cut: no itemSelector for adapter', adapterKind);
    return;
  }

  // 1. Snapshot first — the source HTML disappears in step 2 and we still
  //    want to be able to paste the captured subtree afterward.
  const snapshot = _captureClipboardSnapshot(payload, adapterKind);
  if (!snapshot) {
    _showToast?.('Could not cut this element', { type: 'error' });
    return;
  }
  setClipboardSnapshot(snapshot);
  _emitElementClipboardChanged(snapshot);

  // 2. Remove from source as its OWN undo step ("Cut element"). Paste later
  //    will be a separate undo step. Two-undo cut→paste is the conventional
  //    model and avoids holding a store transaction open across user actions.
  const result = removeNearEid(target.section.html, payload.eid, { itemSelector });
  if (!result.updatedHtml) {
    console.warn('[element-actions] cut: removeNearEid returned null');
    return;
  }
  const mutations = [{ path: target.path, value: result.updatedHtml }];
  const pruned = pruneOverrides(target.section, result.removedEids);
  if (pruned !== target.section) {
    mutations.push({ path: `${target.sectionPath}.overrides`, value: pruned.overrides });
  }
  const overridesChanged = pruned !== target.section;
  store.batch(mutations, 'Cut element');
  eventBus.emit('preview:sync-section-html', {
    instanceId: payload.instanceId,
    syncOverrides: overridesChanged
  });
  if (_showUndoToast) _showUndoToast('Element cut', { id: 'undo-cut-element' });
  else _showToast?.('Element cut', { type: 'info' });
}

function _onPaste({ payload, adapterKind } = {}) {
  if (!payload) return;
  const clipboard = getClipboard();
  if (!clipboard || clipboard.kind !== 'element') {
    _showToast?.('Clipboard is empty', { type: 'info' });
    return;
  }

  const target = resolveSectionByInstance(payload.instanceId);
  if (!target?.section?.html) return;

  // Fail closed on incompatible source/destination: when both the copied
  // element and the paste target declare a specific adapter kind and they
  // differ (e.g. a social link onto a nav link), refuse rather than force it
  // into the wrong repeat container.
  if (clipboard.adapterKind && adapterKind && clipboard.adapterKind !== adapterKind) {
    _showToast?.('Can’t paste that here', { type: 'info' });
    return;
  }

  // Prefer the target click's adapter selector; fall back to the clipboard's
  // selector. When neither resolves a real insertion site the insert fails
  // closed (returns null) — no silent append outside the section root.
  const itemSelector = _itemSelectorFor(adapterKind, payload) || clipboard.itemSelector || null;

  if (!admitRepairAllocation(target.section)) return;
  const inserted = insertSerializedSubtreeNearEid(
    target.section.html,
    clipboard.htmlFragment,
    clipboard.sourceEids,
    {
      editableIdRepairs: target.section.editableIdRepairs,
      nearEid: payload.eid || null,
      position: 'after',
      itemSelector,
      existingIds: _collectProjectIdentityIds()
    }
  );
  if (!inserted.updatedHtml) {
    console.warn('[element-actions] paste: insertSerializedSubtreeNearEid returned null');
    _showToast?.('Could not paste here', { type: 'error' });
    return;
  }

  const mutations = [{ path: target.path, value: inserted.updatedHtml }];
  const overridesPatch = applyOverridesCloneToSection(
    target.section,
    clipboard.sourceOverridesByEid,
    inserted.eidMap
  );
  if (overridesPatch) {
    mutations.push({ path: `${target.sectionPath}.overrides`, value: overridesPatch });
  }
  store.batch(mutations, 'Paste element');
  eventBus.emit('preview:sync-section-html', {
    instanceId: payload.instanceId,
    syncOverrides: !!overridesPatch
  });

  // Surface shared-section pastes prominently — they fan out across pages.
  const sharedType = _detectSharedKind(target.sectionPath);
  if (sharedType) {
    _showToast?.(`Pasted into shared ${sharedType} — visible on all pages`, { type: 'info' });
  } else {
    _showToast?.('Element pasted', { type: 'success' });
  }

  _reselectAfterRender(payload.instanceId, inserted.newPrimaryEid);
}

function _captureClipboardSnapshot(payload, adapterKind) {
  if (!payload?.instanceId || !payload?.eid) return null;
  const target = resolveSectionByInstance(payload.instanceId);
  if (!target?.section?.html) return null;
  const itemSelector = _itemSelectorFor(adapterKind, payload);
  if (!itemSelector) return null;
  const serialized = serializeSubtreeAroundEid(target.section.html, payload.eid, { itemSelector });
  if (!serialized) return null;
  return buildSnapshotFromSerialization({
    section: target.section,
    instanceId: payload.instanceId,
    adapterKind: adapterKind || null,
    itemSelector,
    htmlFragment: serialized.htmlFragment,
    sourceEids: serialized.sourceEids,
    sourcePrimaryEid: serialized.sourcePrimaryEid
  });
}

function _detectSharedKind(sectionPath) {
  if (typeof sectionPath !== 'string') return null;
  if (sectionPath.startsWith('sharedSections.navigation')) return 'navigation';
  if (sectionPath.startsWith('sharedSections.footer')) return 'footer';
  return null;
}

// Re-exported so the keyboard-shortcut layer in shell.js can dispatch the
// same events without going through the right-click context menu.
export function copyElementFromPayload(payload, adapterKind) {
  _onCopy({ payload, adapterKind });
}
export function cutElementFromPayload(payload, adapterKind) {
  _onCut({ payload, adapterKind });
}
export function duplicateElementFromPayload(payload, adapterKind) {
  _onDuplicate({ payload, adapterKind });
}
export function deleteElementFromPayload(payload, adapterKind) {
  return _onDelete({ payload, adapterKind });
}
/**
 * Programmatic cross-container move — the entry a drag-gesture front-end (or a
 * test) calls with a resolved {source, target, position} spec. Mirrors the
 * `element:move-request` event.
 */
export function moveElementAcrossContainers(spec) {
  _onMoveElement(spec);
}
export function pasteElementFromPayload(payload, adapterKind) {
  _onPaste({ payload, adapterKind });
}
export { clearClipboard as _clearElementClipboard };

/** Clear the element clipboard and notify the clipboard tray. */
export function clearElementClipboard() {
  clearClipboard();
  _emitElementClipboardChanged(null);
}

// Same shape for the format painter — exposed so the click handler in
// shell.js can route a paint-mode click directly to applyToTarget.
export function applyPaintFromPayload(payload) {
  if (!payload?.instanceId || !payload?.eid) return { ok: false, reason: 'invalid-target' };
  return _runPaintApply({
    instanceId: payload.instanceId,
    eid: payload.eid,
    editableType: payload.editableType || ''
  });
}

function _onCopyStyle({ payload } = {}) {
  if (!payload?.instanceId || !payload?.eid) return;
  const editableType = payload.editableType || '';
  const entered = enterPaintMode({
    instanceId: payload.instanceId,
    eid: payload.eid,
    editableType,
    rect: payload.rect || null,
    rectIsViewportRelative: true
  });
  if (!entered) {
    _showToast?.('Could not copy this style', { type: 'error' });
    return;
  }
  _showToast?.('Style copied — click another element to paste', { type: 'info' });
}

function _onPasteStyle({ payload } = {}) {
  if (!payload?.instanceId || !payload?.eid) return;
  if (!isPaintModeActive()) {
    _showToast?.('Copy a style first', { type: 'info' });
    return;
  }
  _runPaintApply({
    instanceId: payload.instanceId,
    eid: payload.eid,
    editableType: payload.editableType || ''
  });
}

function _runPaintApply(target) {
  const result = applyPaintToTarget(target);
  if (result.ok) {
    eventBus.emit('preview:sync-section-html', {
      instanceId: target.instanceId,
      syncOverrides: false
    });
    _showToast?.('Style pasted', { type: 'success' });
    return result;
  }
  if (result.reason === 'type-mismatch') {
    const a = _humanizeType(result.sourceType);
    const b = _humanizeType(result.targetType);
    _showToast?.(`Style of ${a} can't be pasted onto ${b}`, { type: 'error' });
  } else if (result.reason === 'same-element') {
    // No-op — clicking the source element should silently stay in paint mode.
  } else if (result.reason === 'no-target-section') {
    _showToast?.('Target section not found', { type: 'error' });
    exitPaintMode();
  }
  return result;
}

function _humanizeType(t) {
  if (!t) return 'this element';
  if (t === 'button') return 'a button';
  if (t === 'image') return 'an image';
  if (t === 'svg') return 'an icon';
  if (t === 'text') return 'text';
  if (t === 'container') return 'a container';
  return `a ${t}`;
}

function _onReorder(payload) {
  if (!payload?.instanceId || !payload.sourceEid || !payload.targetEid) return;
  if (payload.sourceEid === payload.targetEid) return;

  const target = resolveSectionByInstance(payload.instanceId);
  if (!target?.section?.html) return;

  const kind = payload.kind;
  const position = payload.position === 'before' ? 'before' : 'after';

  // Registry-driven (Phase 1): the adapter carries the mover, the history/undo
  // label, and the success-toast text. No per-kind if/else here — a new
  // draggable kind lights up purely by registering a reorderable adapter.
  const adapter = getAdapterByKind(kind);
  if (!adapter || typeof adapter.moveFn !== 'function') {
    _revertOptimisticReorder(
      payload.instanceId,
      new Error(`no reorder adapter for kind "${kind}"`)
    );
    return;
  }
  const label = adapter.historyLabel || 'Reorder element';
  const itemSelector = adapter.reorderItemSelector || adapter.itemSelector;

  let result;
  let updatedHtml;
  try {
    result = adapter.moveFn(target.section.html, payload.sourceEid, payload.targetEid, {
      position,
      itemSelector
    });
    updatedHtml = result?.updatedHtml || null;
    if (updatedHtml && typeof adapter.postMoveNormalize === 'function') {
      updatedHtml = adapter.postMoveNormalize(updatedHtml, {
        kind,
        sourceEid: payload.sourceEid,
        targetEid: payload.targetEid,
        position,
        itemSelector,
        parentSelector: adapter.parentSelector
      });
    }
  } catch (err) {
    _revertOptimisticReorder(payload.instanceId, err);
    return;
  }

  if (!result || !updatedHtml || updatedHtml === target.section.html) {
    _revertOptimisticReorder(payload.instanceId, new Error('reorder produced no change'));
    return;
  }

  if (!store.update(target.path, updatedHtml, label)) {
    _revertOptimisticReorder(payload.instanceId, new Error('reorder commit rejected'));
    return;
  }
  // Phase 20: include `label` so the iframe-side View Transition wrapper can
  // gate on it (only reorder labels morph; text edits and other surgical
  // patches run with the existing direct-mutation path).
  eventBus.emit('preview:sync-section-html', {
    instanceId: payload.instanceId,
    preserveScroll: true,
    label: label,
    syncOverrides: false
  });
  _showToast?.(adapter.toastText || 'Reordered', { type: 'success' });
  _reselectAfterRender(payload.instanceId, result.sourceRootEid || payload.sourceEid);
}

function _onMovePrev(evt) {
  _onMove(evt, 'prev');
}
function _onMoveNext(evt) {
  _onMove(evt, 'next');
}

/**
 * Cross-container element move — "drag anything anywhere". Built over the
 * cut/paste plumbing (serialize → remove → insert → override-migrate) rather
 * than the sibling-reorder engine, so it can move a whole subtree between
 * containers or sections and reallocate eids against the destination pool.
 * Lands as ONE undo entry (`store.batch(..., 'Move element')`).
 *
 * spec: {
 *   sourceInstanceId, sourceEid, sourceKind?, sourceItemSelector?,
 *   targetInstanceId, targetEid, position: 'before'|'after'|'inside', targetItemSelector
 * }
 */
function _onMoveElement(spec = {}) {
  const {
    sourceInstanceId,
    sourceEid,
    sourceKind = '',
    sourceItemSelector: requestedSourceItemSelector,
    targetInstanceId,
    targetEid = null,
    position = 'after',
    targetItemSelector = null
  } = spec;
  const sourceAdapter = sourceKind ? getAdapterByKind(sourceKind) : null;
  const sourceItemSelector = sourceKind
    ? sourceAdapter?.reorderItemSelector || sourceAdapter?.itemSelector
    : requestedSourceItemSelector;
  const effectiveTargetItemSelector =
    position === 'inside' ? null : sourceItemSelector || targetItemSelector;
  if (!sourceInstanceId || !sourceEid || !targetInstanceId) return;

  const fail = (err) =>
    reportError('Move element', err, { user: 'toast', message: "Couldn't move element" });
  if (!sourceItemSelector) {
    fail(
      new Error(
        sourceKind ? `no move adapter for kind "${sourceKind}"` : 'source item selector missing'
      )
    );
    return;
  }

  const src = resolveSectionByInstance(sourceInstanceId);
  const tgt = resolveSectionByInstance(targetInstanceId);
  if (!src?.section?.html || !tgt?.section?.html) return;
  if (!admitRepairAllocation(src.section) || !admitRepairAllocation(tgt.section)) return;

  // 1. Serialize the subtree + snapshot its per-element overrides from source.
  const serialized = serializeSubtreeAroundEid(src.section.html, sourceEid, {
    itemSelector: sourceItemSelector
  });
  if (!serialized) {
    fail(new Error('source subtree did not resolve'));
    return;
  }
  const snapshot = buildSnapshotFromSerialization({
    section: src.section,
    instanceId: sourceInstanceId,
    adapterKind: null,
    itemSelector: sourceItemSelector,
    htmlFragment: serialized.htmlFragment,
    sourceEids: serialized.sourceEids,
    sourcePrimaryEid: serialized.sourcePrimaryEid
  });

  const sameSection = sourceInstanceId === targetInstanceId;

  // 2. Remove from source.
  const removed = removeNearEid(src.section.html, sourceEid, { itemSelector: sourceItemSelector });
  if (!removed.updatedHtml) {
    fail(new Error('source remove failed'));
    return;
  }
  let sourceHtml = removed.updatedHtml;
  if (typeof sourceAdapter?.postMoveNormalize === 'function') {
    sourceHtml = sourceAdapter.postMoveNormalize(sourceHtml, {
      kind: sourceKind,
      sourceEid: null,
      targetEid: null,
      position: 'remove',
      itemSelector: sourceItemSelector,
      parentSelector: sourceAdapter.parentSelector,
      normalizeAll: true
    });
  }
  // pruneOverrides takes the SECTION and returns a new section (or the same when
  // unchanged); drop the moved eids' overrides from the source.
  const prunedSrcSection = pruneOverrides(src.section, removed.removedEids);
  const prunedSrcOverrides = prunedSrcSection.overrides;

  // 3. Insert into target. Same-section moves insert into the JUST-REMOVED html
  //    so the two edits collapse into a single html value for that path.
  const targetHtml = sameSection ? sourceHtml : tgt.section.html;
  const inserted = insertSerializedSubtreeNearEid(
    targetHtml,
    serialized.htmlFragment,
    serialized.sourceEids,
    {
      editableIdRepairs: tgt.section.editableIdRepairs,
      nearEid: targetEid,
      position,
      itemSelector: effectiveTargetItemSelector,
      existingIds: _collectProjectIdentityIds()
    }
  );
  if (!inserted.updatedHtml) {
    fail(new Error('target insert failed'));
    return;
  }
  let insertedHtml = inserted.updatedHtml;
  if (typeof sourceAdapter?.postMoveNormalize === 'function') {
    insertedHtml = sourceAdapter.postMoveNormalize(insertedHtml, {
      kind: sourceKind,
      sourceEid: inserted.newRootEid || inserted.newPrimaryEid,
      targetEid,
      position,
      itemSelector: sourceItemSelector,
      parentSelector: sourceAdapter.parentSelector
    });
  }

  // 4. Migrate overrides under the freshly allocated eids.
  const overridesBase = sameSection ? { overrides: prunedSrcOverrides } : tgt.section;
  const migrated = applyOverridesCloneToSection(
    overridesBase,
    snapshot.sourceOverridesByEid,
    inserted.eidMap
  );

  // 5. Commit as ONE undo entry.
  const mutations = [];
  if (sameSection) {
    mutations.push({ path: src.path, value: insertedHtml });
    const ov = migrated || prunedSrcOverrides;
    if (ov !== src.section.overrides)
      mutations.push({ path: `${src.sectionPath}.overrides`, value: ov });
  } else {
    mutations.push({ path: src.path, value: sourceHtml });
    if (prunedSrcSection !== src.section) {
      mutations.push({ path: `${src.sectionPath}.overrides`, value: prunedSrcOverrides });
    }
    mutations.push({ path: tgt.path, value: insertedHtml });
    if (migrated) mutations.push({ path: `${tgt.sectionPath}.overrides`, value: migrated });
  }
  if (!store.batch(mutations, 'Move element')) {
    fail(new Error('move commit rejected'));
    return;
  }

  // 6. Re-sync both affected sections (per-instance surgical path).
  eventBus.emit('preview:sync-section-html', { instanceId: sourceInstanceId, syncOverrides: true });
  if (!sameSection) {
    eventBus.emit('preview:sync-section-html', {
      instanceId: targetInstanceId,
      syncOverrides: true
    });
  }
  _showToast?.('Element moved', { type: 'success' });
  _reselectAfterRender(targetInstanceId, inserted.newPrimaryEid);
}

/**
 * Keyboard / context-menu reorder: move the selected element's repeat root one
 * slot toward its previous ('prev') or next ('next') filtered sibling. Unlike
 * drag, there is no optimistic pre-move to reconcile and no drag target — the
 * move is relative to the current position. Reuses the adapter registry (mover,
 * label, toast) so it stays in lock-step with the drag commit path.
 */
function _onMove({ payload, adapterKind } = {}, direction) {
  if (!payload?.instanceId || !payload.eid) return;
  const adapter = getAdapterByKind(adapterKind);
  if (!adapter?.reorderable) return;
  const target = resolveSectionByInstance(payload.instanceId);
  if (!target?.section?.html) return;

  const itemSelector = adapter.reorderItemSelector || adapter.itemSelector;
  const siblingSelector = adapter.reorderSiblingSelector || adapter.reorderSelector || itemSelector;

  let result;
  let updatedHtml;
  try {
    result = moveAdjacentSiblingPreservingEids(target.section.html, payload.eid, {
      itemSelector,
      siblingSelector,
      direction
    });
    updatedHtml = result?.updatedHtml || null;
    // Same normalization the drag commit runs (_onReorder): pinned card/gallery
    // grids bind position to ordinal classes, so a bare DOM move is visually
    // inert without it.
    if (updatedHtml && typeof adapter.postMoveNormalize === 'function') {
      updatedHtml = adapter.postMoveNormalize(updatedHtml, {
        kind: adapterKind,
        sourceEid: result.sourceRootEid || payload.eid,
        targetEid: null,
        position: direction,
        itemSelector,
        parentSelector: adapter.parentSelector
      });
    }
  } catch (err) {
    reportError('Element reorder', err, { user: 'toast', message: "Couldn't move — reverted" });
    return;
  }
  // At an edge (no adjacent sibling) the mover returns null: a legitimate no-op,
  // not a failure — stay silent rather than toasting.
  if (!updatedHtml || updatedHtml === target.section.html) return;

  const label = adapter.historyLabel || 'Reorder element';
  if (!store.update(target.path, updatedHtml, label)) return;
  eventBus.emit('preview:sync-section-html', {
    instanceId: payload.instanceId,
    preserveScroll: true,
    label,
    syncOverrides: false
  });
  _showToast?.(adapter.toastText || 'Reordered', { type: 'success' });
  _reselectAfterRender(payload.instanceId, result.sourceRootEid || payload.eid);
}

/**
 * Reconcile a failed reorder commit. By the time _onReorder runs, the iframe
 * has already optimistically pre-moved the source element and reset its own
 * drag state (see reorder-preview.js _commitReorder), so `cancel-reorder` is a
 * no-op — there is no live drag to cancel. The only correct recovery is a
 * CANONICAL section resync from committed state: emit preview:sync-section-html
 * WITHOUT a `label` so the iframe runs the direct-mutation path (no View
 * Transition morph) and overwrites the stale optimistic DOM with the real HTML.
 * Then surface a low-key toast (deduped by reportError).
 */
function _revertOptimisticReorder(instanceId, err) {
  if (!instanceId) return;
  eventBus.emit('preview:sync-section-html', {
    instanceId,
    preserveScroll: true,
    syncOverrides: false
  });
  reportError('Element reorder', err, {
    user: 'toast',
    message: "Couldn't reorder — reverted"
  });
}

function _itemSelectorFor(adapterKind, payload) {
  // Registry-driven (Phase 1): resolve the repeat-root selector from the
  // adapter so duplicate / delete / visibility act on the correct unit for
  // ANY kind (nav, social, and future FAQ/card/gallery), not the clicked child.
  const adapter = getAdapterByKind(adapterKind);
  if (adapter?.itemSelector) return adapter.itemSelector;
  if (payload?.eid) return `[data-pb-eid="${escapeForAttrSelector(payload.eid)}"]`;
  return null;
}

function _visibilityTargetFor(sectionHtml, selectedEid, adapterKind, editableIdRepairs) {
  if (!sectionHtml || !selectedEid) return null;
  const itemSelector = getAdapterByKind(adapterKind)?.itemSelector || null;
  if (!itemSelector) return { eid: selectedEid, updatedHtml: null };
  if (typeof DOMParser === 'undefined') return { eid: selectedEid, updatedHtml: null };

  const doc = new DOMParser().parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return { eid: selectedEid, updatedHtml: null };

  const selected = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(selectedEid)}"]`);
  const item = selected?.closest(itemSelector);
  if (!item) return { eid: selectedEid, updatedHtml: null };

  let eid = item.getAttribute('data-pb-eid');
  if (eid) return { eid, updatedHtml: null };

  eid = _nextEid(root, editableIdRepairs);
  item.setAttribute('data-pb-eid', eid);
  return { eid, updatedHtml: root.innerHTML };
}

function _nextEid(root, editableIdRepairs) {
  const used = new Set(
    Array.from(root.querySelectorAll('[data-pb-eid]')).map((el) => el.getAttribute('data-pb-eid'))
  );
  let counter = 0;
  for (const eid of reservedEditableIds(editableIdRepairs)) used.add(eid);
  let eid;
  do {
    eid = `e${++counter}`;
  } while (used.has(eid));
  return eid;
}

function _withElementHidden(currentOverrides, eid, hidden) {
  const overrides = { ...(currentOverrides || {}) };
  const elements = { ...(overrides.elements || {}) };
  const element = { ...(elements[eid] || {}) };
  if (hidden) {
    element.hidden = true;
  } else {
    delete element.hidden;
  }

  if (Object.keys(element).length > 0) elements[eid] = element;
  else delete elements[eid];

  if (Object.keys(elements).length > 0) overrides.elements = elements;
  else delete overrides.elements;

  return Object.keys(overrides).length > 0 ? overrides : null;
}

function _anchorRectFromPayload(payload) {
  if (payload?.point && Number.isFinite(payload.point.top) && Number.isFinite(payload.point.left)) {
    return {
      top: payload.point.top,
      left: payload.point.left,
      width: 1,
      height: 1
    };
  }
  return payload?.rect || { top: 0, left: 0, width: 1, height: 1 };
}

function _reselectAfterRender(instanceId, eid, afterSelect) {
  if (!instanceId || !eid) return;
  if (_pendingReselectCancel) {
    _pendingReselectCancel();
    _pendingReselectCancel = null;
  }

  let cancelled = false;
  let fired = false;
  let timer = null;

  const cancel = () => {
    cancelled = true;
    if (_pendingReselectCancel === cancel) {
      _pendingReselectCancel = null;
      _pendingReselectInstanceId = null;
    }
    clearTimeout(timer);
    offBridgeReady();
  };
  _pendingReselectCancel = cancel;
  _pendingReselectInstanceId = instanceId;

  const fire = () => {
    if (cancelled || fired) return;
    fired = true;
    clearTimeout(timer);
    offBridgeReady();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        bridge.send('reselect-editable', { instanceId, eid });
        bridge.send('query-element-rect', { instanceId, eid });
        if (typeof afterSelect === 'function') {
          requestAnimationFrame(() => {
            if (!cancelled) afterSelect();
            cancel();
          });
        } else cancel();
      });
    });
  };

  const offBridgeReady = eventBus.once('iframe:bridge-ready', fire);
  timer = setTimeout(fire, RESELECT_AFTER_RENDER_FALLBACK_MS);
}
